/**
 * What a text costs to send, counted the way core-api counts it.
 *
 * Its own module because the day's limit is counted in parts, not texts (ADR-098), so these three
 * have to agree with `messages.segments` and `messages.body_to_send` on the server. Pure, and
 * unit-tested in `test/conversations.test.ts`.
 */

import type { TextCompose } from "@/lib/types";

/** The characters a text can use at 160 to a part (GSM 03.38), as core-api's `messages.segments`
 *  has them. Anything else, an emoji or a curly quote, makes the whole text travel at 70 to a part. */
const GSM7 = new Set(
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ ÆæßÉ!\"#¤%&'()*+,-./0123456789:;<=>?" +
    "¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà",
);
/** These take two places each: an escape, then the character. */
const GSM7_TWO = new Set("^{}\\[~]|€\f");

/**
 * How many parts a text travels in, counted as core-api counts them (`messages.segments`), since the
 * day's limit is counted in them: one of up to 160 characters, or parts of 153 once it is longer,
 * where `^ { } [ ] ~ | \ €` take two places each. One character outside that alphabet, an emoji or a
 * curly quote, sends the whole text as UCS-2: one of up to 70 UTF-16 units, or parts of 67, an emoji
 * taking two.
 */
export function smsSegments(text: string): number {
  if (text.length === 0) return 0;
  let places = 0;
  for (const ch of text) {
    if (GSM7_TWO.has(ch)) places += 2;
    else if (GSM7.has(ch)) places += 1;
    else return text.length <= 70 ? 1 : Math.ceil(text.length / 67);
  }
  return places <= 160 ? 1 : Math.ceil(places / 153);
}

/** A text as core-api sends it (`messages.body_to_send`): what was written, and under the first text
 *  to a number the opt-out line from its compose answer, unless the writer has said so already. */
export function smsBody(text: string, optOutLine: string | null | undefined): string {
  const body = text.trim();
  return optOutLine && !body.toLowerCase().includes("reply stop") ? `${body}\n${optOutLine}` : body;
}

/** How much may be typed in a text: core-api's `max_chars`, which counts the opt-out line under a
 *  first text too (a new line, then the line). The box always leaves it room, though core-api leaves
 *  the line off a text that already says "reply STOP". */
export function smsRoom(compose: Pick<TextCompose, "max_chars" | "opt_out_line">): number {
  return compose.opt_out_line ? compose.max_chars - compose.opt_out_line.length - 1 : compose.max_chars;
}
